import React, { useState, useEffect, useCallback } from 'react';
import { Invoice, Family } from '@/types/models';
import { getCurrentUser } from '@/lib/auth';
import { tables } from '@/lib/supabase';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import InvoiceList from './InvoiceList';
import InvoiceDialog from './InvoiceDialog';
import DeleteInvoiceDialog from './DeleteInvoiceDialog';
import { useInvoiceOperations } from './hooks/useInvoiceOperations';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertTriangle } from 'lucide-react';
import { containerVariants, itemVariants } from '@/utils/animations';
import { getInvoices } from '@/services/invoiceService';
import { eventEmitter, EVENTS } from '@/utils/eventEmitter';

interface InvoicesContainerProps {
  className?: string;
  initialStatuses?: string[];
}

const InvoicesContainer: React.FC<InvoicesContainerProps> = ({ className, initialStatuses }) => {
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [families, setFamilies] = useState<Family[]>([]);
  const [loading, setLoading] = useState(true);
  const [connectionError, setConnectionError] = useState(false);

  const {
    selectedInvoice,
    isAddInvoiceDialogOpen,
    setIsAddInvoiceDialogOpen,
    isEditInvoiceDialogOpen,
    setIsEditInvoiceDialogOpen,
    isDeleteInvoiceDialogOpen,
    setIsDeleteInvoiceDialogOpen,
    handleAddInvoice,
    handleEditInvoice,
    handleDeleteInvoice,
    handleCreateInvoice,
    handleUpdateInvoice,
    handleConfirmDelete,
  } = useInvoiceOperations(organizationId);

  useEffect(() => {
    (async () => {
      const user = await getCurrentUser();
      setOrganizationId(user?.user_metadata?.organization_id ?? null);
    })();
  }, []);

  const loadData = useCallback(async (orgId: string) => {
    try {
      setLoading(true);
      const {
        data: familiesData,
        error: familiesError
      } = await tables.families().select('*').eq('organization_id', orgId);
      
      if (familiesError) {
        throw familiesError;
      }
      
      // Use getInvoices to fetch invoices with family data included
      const invoicesData = await getInvoices(orgId);
      
      setInvoices(invoicesData);
      setFamilies(familiesData as Family[]);
      setConnectionError(false);
    } catch (error) {
      console.error("Failed to load data:", error);
      setConnectionError(true);
      toast.error("Database connection failed", {
        description: "Unable to fetch invoice data. Please try again later."
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshData = useCallback(() => {
    if (organizationId) {
      loadData(organizationId);
    }
  }, [organizationId, loadData]);

  useEffect(() => {
    if (organizationId) {
      loadData(organizationId);
    }
  }, [organizationId]);

  // Listen for payment/invoice update events to refresh data
  useEffect(() => {
    const handleRefreshInvoices = () => {
      refreshData();
    };

    eventEmitter.on(EVENTS.REFRESH_INVOICES, handleRefreshInvoices);
    eventEmitter.on(EVENTS.PAYMENT_RECORDED, handleRefreshInvoices);

    return () => {
      eventEmitter.off(EVENTS.REFRESH_INVOICES, handleRefreshInvoices);
      eventEmitter.off(EVENTS.PAYMENT_RECORDED, handleRefreshInvoices);
    };
  }, [refreshData]);

  const onInvoiceCreated = async (data: any) => {
    const newInvoice = await handleCreateInvoice(data);
    if (newInvoice) {
      setInvoices(prev => [...prev, newInvoice]);
    }
  };

  const onInvoiceUpdated = async (data: any) => {
    const updatedInvoice = await handleUpdateInvoice(data);
    if (updatedInvoice) {
      setInvoices(prev => prev.map(inv => inv.id === updatedInvoice.id ? updatedInvoice : inv));
    }
  };

  const onInvoiceDeleted = async () => {
    const success = await handleConfirmDelete();
    if (success && selectedInvoice) {
      setInvoices(prev => prev.filter(inv => inv.id !== selectedInvoice.id));
    }
  };

  return (
    <motion.div className={className} variants={containerVariants} initial="hidden" animate="visible">
      {connectionError && (
        <motion.div variants={itemVariants}>
          <Alert variant="default" className="bg-amber-50 border-amber-200">
            <AlertTriangle className="h-4 w-4 text-amber-800" />
            <AlertTitle className="text-amber-800">Connection error</AlertTitle>
            <AlertDescription className="text-amber-700">
              Unable to connect to the database. Please check your internet connection or try again later.
            </AlertDescription>
          </Alert>
        </motion.div>
      )}

      <motion.div variants={itemVariants}>
        {loading ? (
          <div className="flex justify-center items-center min-h-[300px] bg-white/50 backdrop-blur-sm rounded-xl border border-gray-100/60 shadow-sm p-6">
            <div className="h-8 w-8 rounded-full border-2 border-hivebubble-coral/30 border-t-hivebubble-coral animate-spin mr-2"></div>
            <div className="text-hivebubble-slate/80 font-medium">Loading invoices...</div>
          </div>
        ) : (
          <InvoiceList
            invoices={invoices}
            families={families}
            onAddInvoice={handleAddInvoice}
            onEditInvoice={handleEditInvoice}
            onDeleteInvoice={handleDeleteInvoice}
            initialStatuses={initialStatuses}
          />
        )}
      </motion.div>

      <InvoiceDialog
        open={isAddInvoiceDialogOpen}
        onOpenChange={setIsAddInvoiceDialogOpen}
        families={families}
        onSubmit={onInvoiceCreated}
        title="Create New Invoice"
      />

      <InvoiceDialog
        open={isEditInvoiceDialogOpen}
        onOpenChange={setIsEditInvoiceDialogOpen}
        families={families}
        invoice={selectedInvoice || undefined}
        onSubmit={onInvoiceUpdated}
        title="Edit Invoice"
      />

      <DeleteInvoiceDialog
        open={isDeleteInvoiceDialogOpen}
        onOpenChange={setIsDeleteInvoiceDialogOpen}
        invoice={selectedInvoice}
        onConfirm={onInvoiceDeleted}
      />
    </motion.div>
  );
};

export default InvoicesContainer;
